import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import SortableTh from '../../components/common/SortableTh';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTableSort } from '../../hooks/useTableSort';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import KkpPlanningPageActions from '../../components/kkp/KkpPlanningPageActions';
import CommercialSelect from '../../components/CommercialSelect';
import { AppFilterBar, AppFilterInput, AppFilterSelect, AppFilterCommercialSelect } from '../../components/filters';
import { notify } from '../../components/common/AppToast';
import clientAnnuaireService from '../../services/clientAnnuaireService';
import {
  AppBadge,
  AppButton,
  AppInput,
  AppPagination,
  AppPillSwitch,
  AppStatusBadge,
} from '../../components/ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import ClientAnnuaireExportModal from './ClientAnnuaireExportModal';
import ClientIdentifiantsPills from './ClientIdentifiantsPills';
import ClientIdentifiantsDrawer from './ClientIdentifiantsDrawer';
import '../../styles/kkp-planning-page.scss';
import '../../styles/client-annuaire.scss';

const YES_NO = [
  { value: '1', label: 'Oui' },
  { value: '0', label: 'Non' },
];

const CLIENT_STATUTS = [
  { value: 'client', label: 'Client', variant: 'success' },
  { value: 'ancien_client', label: 'Ancien client', variant: 'warning' },
  { value: 'prospect', label: 'Prospect', variant: 'secondary' },
];

const CLIENT_STATUT_ORDER = CLIENT_STATUTS.map((item) => item.value);

const CLIENT_STATUT_META = Object.fromEntries(CLIENT_STATUTS.map((item) => [item.value, item]));

const CLIENT_STATUT_TITLE = 'Client : facture ou commande valide sur l’année en cours ou N-1. Ancien client : activité plus ancienne. Prospect : aucune facture ni commande.';

const nextClientStatut = (current) => {
  const index = CLIENT_STATUT_ORDER.indexOf(current);
  return CLIENT_STATUT_ORDER[(index + 1) % CLIENT_STATUT_ORDER.length];
};

const MISSING_IDS = [
  { value: 'hubspot', label: 'Sans HubSpot' },
  { value: 'drive', label: 'Sans Drive' },
  { value: 'rag', label: 'Sans RAG' },
];

const MISSING_FIELDS = [
  { value: 'a_completer', label: 'À compléter (l’un des quatre)' },
  { value: 'client', label: 'Sans client' },
  { value: 'commercial', label: 'Sans commercial' },
  { value: 'gerant', label: 'Sans gérant' },
  { value: 'contact', label: 'Sans contact' },
];

const RAPPROCHEMENT = [
  { value: 'complet', label: 'Complets' },
  { value: 'partiel', label: 'Partiels' },
  { value: 'a_verifier', label: 'À vérifier' },
  { value: 'absent', label: 'Sans identifiant' },
];

const MARQUES = [
  { value: 'iti', label: 'itiConseil' },
  { value: 'kkp', label: 'Koikispass' },
  { value: 'klub', label: 'Klub' },
];

const BRANDS = {
  iti: { label: 'itiConseil', short: 'iti', color: '#eb6608' },
  kkp: { label: 'Koikispass', short: 'KKP', color: '#e30613' },
  klub: { label: 'Klub', short: 'Klub', color: '#2563eb' },
};

const DURATION_UNITS = [
  { value: 'y', label: 'an(s)' },
  { value: 'm', label: 'mois' },
  { value: 'w', label: 'semaine(s)' },
  { value: 'd', label: 'jour(s)' },
];

const TABLE_SORT_COLUMNS = {
  client: { type: 'string', getValue: (row) => row.nom },
  commercial: { type: 'string', getValue: (row) => row.commercial_nom },
  gerant: { type: 'string', getValue: (row) => row.gerant_nom },
  contact: { type: 'string', getValue: (row) => `${row.contact_email || ''} ${row.contact_phone || ''}` },
  client_statut: {
    type: 'string',
    getValue: (row) => CLIENT_STATUT_META[row.client_statut]?.label || row.client_statut || '',
  },
  contrat: {
    type: 'string',
    getValue: (row) => (row.active_contracts || []).map((item) => item.ref).join(' ') || (row.has_active_contract ? 'Oui' : ''),
  },
  commande: {
    type: 'date',
    getValue: (row) => row.last_order_at || (row.last_orders || [])[0]?.date || '',
  },
};

const PRODUIT_SORT_COLUMNS = {
  ref: { type: 'string', getValue: (row) => row.product_ref },
  label: { type: 'string', getValue: (row) => row.product_label },
  price: { type: 'number', getValue: (row) => Number(row.price_ht) || 0 },
  tva: { type: 'number', getValue: (row) => Number(row.tva_tx) || 0 },
  etat: { type: 'string', getValue: (row) => (row.is_tosell ? '1' : '0') },
  marques: {
    type: 'string',
    getValue: (row) => [row.is_iti && 'iti', row.is_kkp && 'kkp', row.is_klub && 'klub'].filter(Boolean).join(' '),
  },
  duree: { type: 'number', getValue: (row) => Number(row.duration_value) || 0 },
  synced: { type: 'date', getValue: (row) => row.synced_at || '' },
};

const ETAT_VENTE = [
  { value: '1', label: 'En vente' },
  { value: '0', label: 'Hors vente' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDate(value);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPriceHt = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

const DOLIBARR_WEB_BASE = (process.env.REACT_APP_DOLIBARR_URL || 'https://dolibarr2.iti-conseil.com').replace(/\/+$/, '');

const dolibarrCardUrl = (socid) => {
  const id = Number(socid);
  if (!Number.isFinite(id) || id <= 0) return '';
  return `${DOLIBARR_WEB_BASE}/comm/card.php?socid=${id}&save_lastsearch_values=1`;
};

const firstOrEmpty = (values) => (Array.isArray(values) && values.length ? values[0] : '');

const contractsForRow = (row) => {
  if (Array.isArray(row.active_contracts) && row.active_contracts.length > 0) {
    return row.active_contracts;
  }
  if (row.active_contract_label) {
    const marque = row.is_klub ? 'klub' : (row.is_iti ? 'iti' : 'kkp');
    return [{ ref: row.active_contract_label, until: row.active_contract_until, marque, label: row.active_contract_label }];
  }
  return [];
};

const ordersForRow = (row) => {
  if (Array.isArray(row.last_orders) && row.last_orders.length > 0) {
    return row.last_orders;
  }
  return [];
};

const ClientAnnuairePage = () => {
  const { ask, modal } = useAppConfirm();
  const [tab, setTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [marque, setMarque] = useState([]);
  const [clientStatut, setClientStatut] = useState([]);
  const [hasContract, setHasContract] = useState([]);
  const [hasRecent, setHasRecent] = useState([]);
  const [disappeared, setDisappeared] = useState([]);
  const [commercialFilter, setCommercialFilter] = useState([]);
  const [productFilter, setProductFilter] = useState([]);
  const [missingId, setMissingId] = useState([]);
  const [missingField, setMissingField] = useState([]);
  const [rapprochement, setRapprochement] = useState([]);
  const [page, setPage] = useState(1);
  const [savingId, setSavingId] = useState(null);
  const [ficheRow, setFicheRow] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [identifiantsStats, setIdentifiantsStats] = useState({ total: 0, complets: 0, a_verifier: 0 });
  const [produits, setProduits] = useState([]);
  const [syncingProduits, setSyncingProduits] = useState(false);

  const commercialById = useMemo(
    () => Object.fromEntries(commerciaux.map((item) => [String(item.id), item])),
    [commerciaux],
  );
  const commercialOptions = useMemo(
    () => commerciaux.map((item) => ({ value: String(item.id), label: item.nom })),
    [commerciaux],
  );
  const productOptions = useMemo(
    () => produits
      .filter((produit) => !produit.is_excluded)
      .map((produit) => ({
        value: produit.product_ref,
        label: produit.product_label && produit.product_label !== produit.product_ref
          ? `${produit.product_ref} — ${produit.product_label}`
          : produit.product_ref,
      })),
    [produits],
  );

  const filterParams = useMemo(() => {
    const params = {};
    const q = search.trim();
    if (q) params.q = q;
    if (marque.length) {
      params.marque = marque.map((m) => String(m).trim()).filter(Boolean).join(',');
    }
    if (firstOrEmpty(clientStatut) !== '') params.client_statut = firstOrEmpty(clientStatut);
    if (firstOrEmpty(hasContract) !== '') params.has_active_contract = firstOrEmpty(hasContract);
    if (firstOrEmpty(hasRecent) !== '') params.has_recent_order = firstOrEmpty(hasRecent);
    if (firstOrEmpty(disappeared) !== '') params.disappeared = firstOrEmpty(disappeared);
    const commercialNoms = commercialFilter
      .map((id) => commercialById[String(id)]?.nom)
      .filter(Boolean);
    if (commercialNoms.length) {
      params.commercial = commercialNoms.join(',');
    }
    if (productFilter.length) {
      params.product_ref = productFilter.map((ref) => String(ref).trim()).filter(Boolean).join(',');
    }
    if (missingId.length) {
      params.identifiant_manquant = missingId.map((v) => String(v).trim()).filter(Boolean).join(',');
    }
    if (missingField.length) {
      params.champ_manquant = missingField.map((v) => String(v).trim()).filter(Boolean).join(',');
    }
    if (rapprochement.length) {
      params.rapprochement_statut = rapprochement.map((v) => String(v).trim()).filter(Boolean).join(',');
    }
    return params;
  }, [search, marque, clientStatut, hasContract, hasRecent, disappeared, commercialById, commercialFilter, productFilter, missingId, missingField, rapprochement]);

  const queryParams = useMemo(() => ({
    page,
    limit: 50,
    ...filterParams,
  }), [page, filterParams]);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clientAnnuaireService.list(queryParams);
      setClients(data.clients);
      setPagination(data.pagination);
      if (data.identifiants_stats) {
        setIdentifiantsStats(data.identifiants_stats);
      }
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les clients');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const loadProduits = useCallback(async () => {
    try {
      setProduits(await clientAnnuaireService.listProduits());
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les produits');
    }
  }, []);

  useEffect(() => {
    clientAnnuaireService.listCommerciaux().then(setCommerciaux).catch(() => setCommerciaux([]));
    loadProduits();
  }, [loadProduits]);

  useEffect(() => {
    if (tab === 'clients') {
      loadClients();
    }
  }, [tab, loadClients]);

  useEffect(() => {
    if (tab === 'produits') {
      loadProduits();
    }
  }, [tab, loadProduits]);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(clients, TABLE_SORT_COLUMNS);

  const patchClient = async (id, body) => {
    setSavingId(id);
    try {
      const updated = await clientAnnuaireService.update(id, body);
      setClients((prev) => prev.map((row) => (row.id === id ? updated : row)));
    } catch (err) {
      notify.error(err.message || 'Enregistrement impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const stats = await clientAnnuaireService.sync();
      notify.success(`Synchro OK — ${stats.processed ?? 0} tiers, ${stats.created ?? 0} créés`);
      await loadClients();
    } catch (err) {
      notify.error(err.message || 'Synchronisation impossible');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncProduits = async () => {
    setSyncingProduits(true);
    try {
      const { stats, produits: next } = await clientAnnuaireService.syncProduits();
      if (next.length) {
        setProduits(next);
      } else {
        await loadProduits();
      }
      notify.success(
        `Catalogue OK — ${stats.fetched ?? 0} Dolibarr, ${stats.created ?? 0} créés, ${stats.updated ?? 0} mis à jour`,
      );
    } catch (err) {
      notify.error(err.message || 'Synchronisation du catalogue impossible');
    } finally {
      setSyncingProduits(false);
    }
  };

  const handleExport = async (columns) => {
    setExporting(true);
    try {
      const filters = { ...filterParams };
      if (sort.key) {
        filters.sort = sort.key;
        filters.sort_dir = sort.direction;
      }
      filters.columns = columns.join(',');
      await clientAnnuaireService.exportCsv(filters);
      const total = pagination.total;
      notify.success(
        total > 0
          ? `CSV exporté — ${total} client${total > 1 ? 's' : ''} (mêmes filtres que le tableau)`
          : 'CSV exporté (mêmes filtres que le tableau)',
      );
      setExportOpen(false);
    } catch (err) {
      notify.error(err.message || 'Export impossible');
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setMarque([]);
    setClientStatut([]);
    setHasContract([]);
    setHasRecent([]);
    setDisappeared([]);
    setCommercialFilter([]);
    setProductFilter([]);
    setMissingId([]);
    setMissingField([]);
    setRapprochement([]);
    setPage(1);
  };

  const overridden = (row, field) => row[field] !== row[`${field}_auto`];
  const hasActiveFilters = Boolean(
    search
    || marque.length
    || clientStatut.length
    || hasContract.length
    || hasRecent.length
    || disappeared.length
    || commercialFilter.length
    || productFilter.length
    || missingId.length
    || missingField.length
    || rapprochement.length,
  );

  return (
    <>
    <MasterLayout>
      <Breadcrumb
        title="Base clients/Prospects"
        subtitle={tab === 'clients'
          ? `${identifiantsStats.total || pagination.total} clients · ${identifiantsStats.complets || 0} complets · ${identifiantsStats.a_verifier || 0} à vérifier`
          : 'Clients Dolibarr, marques et commerciaux'}
        actions={(
          <KkpPlanningPageActions
            actions={tab === 'clients' ? [
              {
                key: 'sync',
                label: syncing ? 'Synchronisation…' : 'Synchroniser',
                icon: 'mdi:sync',
                onClick: handleSync,
                disabled: syncing,
                loading: syncing,
              },
            ] : [
              {
                key: 'sync-catalog',
                label: syncingProduits ? 'Synchronisation…' : 'Synchroniser le catalogue',
                icon: 'mdi:sync',
                onClick: handleSyncProduits,
                disabled: syncingProduits,
                loading: syncingProduits,
              },
            ]}
            menuItems={tab === 'clients' ? [
              {
                key: 'export',
                label: 'Exporter CSV',
                icon: 'mdi:download',
                onClick: () => setExportOpen(true),
              },
            ] : []}
          />
        )}
      />
      <div className="client-annuaire-page">
        {tab === 'clients' ? (
          <AppFilterBar onReset={resetFilters} resetDisabled={!hasActiveFilters}>
              <AppFilterInput
                id="annuaire-search"
                label="Recherche"
                placeholder="Client, gérant, contact, commercial"
                value={search}
                onChange={(value) => { setSearch(value); setPage(1); }}
              />
              <AppFilterCommercialSelect
                id="annuaire-commercial"
                value={commercialFilter}
                onChange={(value) => { setCommercialFilter(value); setPage(1); }}
                options={commercialOptions}
                multiple
                placeholder="Tous"
              />
              <AppFilterSelect
                label="Marque"
                value={marque}
                onChange={(value) => { setMarque(value); setPage(1); }}
                options={MARQUES}
                multiple
                placeholder="Toutes"
              />
              <AppFilterSelect
                label="Produit"
                value={productFilter}
                onChange={(value) => { setProductFilter(value); setPage(1); }}
                options={productOptions}
                multiple
                placeholder="Tous"
                emptyLabel="Aucun produit"
                col="col-6 col-md-4 col-lg-3"
              />
              <AppFilterSelect
                label="Statut"
                value={clientStatut}
                onChange={(value) => { setClientStatut(value); setPage(1); }}
                options={CLIENT_STATUTS.map(({ value, label }) => ({ value, label }))}
                multiple={false}
                placeholder="Tous"
              />
              <AppFilterSelect
                label="Contrat en cours"
                value={hasContract}
                onChange={(value) => { setHasContract(value); setPage(1); }}
                options={YES_NO}
                multiple={false}
                placeholder="Tous"
              />
              <AppFilterSelect
                label="Commande < 12 mois"
                value={hasRecent}
                onChange={(value) => { setHasRecent(value); setPage(1); }}
                options={YES_NO}
                multiple={false}
                placeholder="Tous"
              />
              <AppFilterSelect
                label="Disparu Dolibarr"
                value={disappeared}
                onChange={(value) => { setDisappeared(value); setPage(1); }}
                options={YES_NO}
                multiple={false}
                placeholder="Tous"
              />
              <AppFilterSelect
                label="Champ manquant"
                value={missingField}
                onChange={(value) => { setMissingField(value); setPage(1); }}
                options={MISSING_FIELDS}
                multiple
                placeholder="Tous"
              />
              <AppFilterSelect
                label="Identifiant manquant"
                value={missingId}
                onChange={(value) => { setMissingId(value); setPage(1); }}
                options={MISSING_IDS}
                multiple
                placeholder="Tous"
              />
              <AppFilterSelect
                label="Rapprochement"
                value={rapprochement}
                onChange={(value) => { setRapprochement(value); setPage(1); }}
                options={RAPPROCHEMENT}
                multiple
                placeholder="Tous"
              />
            </AppFilterBar>
        ) : null}

            <div className="card kkp-planning-results-card">
              <div className="kkp-planning-view-toolbar">
                <AppPillSwitch
                  ariaLabel="Vues de la base clients/Prospects"
                  value={tab}
                  onChange={setTab}
                  options={[
                    {
                      value: 'clients',
                      label: 'Clients',
                      icon: 'mdi:account-group-outline',
                      hint: 'Base clients/Prospects Dolibarr',
                      count: pagination.total,
                    },
                    {
                      value: 'produits',
                      label: 'Correspondance produits',
                      icon: 'mdi:package-variant-closed',
                      hint: 'Catalogue Dolibarr synchronisé (refs, prix, marques)',
                      count: produits.length,
                    },
                  ]}
                />
              </div>
              {tab === 'clients' ? (
                <>
              <div className="card-body p-0">
                {loading && clients.length === 0 ? (
                  <KkpPlanningEmptyState loading />
                ) : !loading && clients.length === 0 ? (
                  <KkpPlanningEmptyState
                    message={hasActiveFilters
                      ? 'Aucun client ne correspond à ces filtres.'
                      : 'Aucun client. Lancez une synchronisation.'}
                    actions={hasActiveFilters ? [{
                      key: 'reset',
                      label: 'Réinitialiser les filtres',
                      icon: 'mdi:filter-off-outline',
                      onClick: resetFilters,
                    }] : [{
                      key: 'sync',
                      label: 'Synchroniser',
                      icon: 'mdi:sync',
                      variant: 'primary',
                      onClick: handleSync,
                    }]}
                  />
                ) : (
                  <StickyTableResponsive>
                    <table className="table table-hover mb-0 kkp-planning-table kkp-iti-list-table">
                      <thead>
                        <tr>
                          <SortableTh
                            sortKey="client"
                            label="Client"
                            className="client-annuaire-col-client"
                            sort={sort}
                            onSort={toggleSort}
                            getSortIcon={getSortIcon}
                          />
                          <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                          <SortableTh
                            sortKey="gerant"
                            label="Gérant"
                            className="client-annuaire-col-gerant"
                            sort={sort}
                            onSort={toggleSort}
                            getSortIcon={getSortIcon}
                          />
                          <SortableTh sortKey="contact" label="Contact" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                          <SortableTh
                            sortKey="client_statut"
                            label="Statut"
                            title={CLIENT_STATUT_TITLE}
                            sort={sort}
                            onSort={toggleSort}
                            getSortIcon={getSortIcon}
                          />
                          <th>itiConseil</th>
                          <th>Koikispass</th>
                          <th>Klub</th>
                          <SortableTh
                            sortKey="contrat"
                            label="Contrat en cours"
                            className="client-annuaire-col-contrat"
                            sort={sort}
                            onSort={toggleSort}
                            getSortIcon={getSortIcon}
                          />
                          <SortableTh
                            sortKey="commande"
                            label="Dernière commande"
                            className="client-annuaire-col-commande"
                            title="Produits des 3 dernières commandes Dolibarr valides, avec leur date."
                            sort={sort}
                            onSort={toggleSort}
                            getSortIcon={getSortIcon}
                          />
                          <th>Identifiants</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((row) => (
                          <tr key={row.id} className={row.disappeared_from_dolibarr ? 'table-warning' : undefined}>
                            <td className="fw-medium client-annuaire-col-client">
                              <InlineText
                                value={row.nom}
                                disabled={savingId === row.id}
                                onSave={(value) => patchClient(row.id, { nom_override: value || null })}
                              />
                              <LegalIds
                                row={row}
                                onUpdated={(updated) => {
                                  setClients((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
                                }}
                              />
                            </td>
                            <td>
                              <CommercialCell
                                row={row}
                                commerciaux={commerciaux}
                                disabled={savingId === row.id}
                                onSave={(nom) => patchClient(row.id, { commercial_nom_override: nom || null })}
                              />
                            </td>
                            <td className="client-annuaire-col-gerant">
                              <InlineText
                                value={row.gerant_nom}
                                disabled={savingId === row.id}
                                onSave={(value) => patchClient(row.id, { gerant_nom_override: value || null })}
                              />
                            </td>
                            <td>
                              <ContactCell
                                row={row}
                                disabled={savingId === row.id}
                                onSaveEmail={(value) => patchClient(row.id, { contact_email_override: value || null })}
                                onSavePhone={(value) => patchClient(row.id, { contact_phone_override: value || null })}
                              />
                            </td>
                            <td>
                              <ClientStatutBadge
                                value={row.client_statut}
                                autoValue={row.client_statut_auto}
                                overridden={overridden(row, 'client_statut')}
                                disabled={savingId === row.id}
                                title={CLIENT_STATUT_TITLE}
                                onCycle={() => {
                                  const next = nextClientStatut(row.client_statut);
                                  patchClient(row.id, {
                                    client_statut_override: next === row.client_statut_auto ? null : next,
                                  });
                                }}
                              />
                            </td>
                            <td>
                              <BrandChip
                                brand="iti"
                                on={row.is_iti}
                                overridden={overridden(row, 'is_iti')}
                                disabled={savingId === row.id}
                                onToggle={() => patchClient(row.id, { is_iti_override: !row.is_iti })}
                              />
                            </td>
                            <td>
                              <BrandChip
                                brand="kkp"
                                on={row.is_kkp}
                                overridden={overridden(row, 'is_kkp')}
                                disabled={savingId === row.id}
                                onToggle={() => patchClient(row.id, { is_kkp_override: !row.is_kkp })}
                              />
                            </td>
                            <td>
                              <BrandChip
                                brand="klub"
                                on={row.is_klub}
                                overridden={overridden(row, 'is_klub')}
                                disabled={savingId === row.id}
                                onToggle={() => patchClient(row.id, { is_klub_override: !row.is_klub })}
                              />
                            </td>
                            <td className="client-annuaire-col-contrat">
                              <ContractBadges contracts={contractsForRow(row)} />
                            </td>
                            <td className="client-annuaire-col-commande">
                              <OrderBadges orders={ordersForRow(row)} />
                            </td>
                            <td>
                              {row.disappeared_from_dolibarr ? (
                                <AppBadge variant="warning">Disparu</AppBadge>
                              ) : (
                                <ClientIdentifiantsPills row={row} onOpen={() => setFicheRow(row)} />
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-inline-flex gap-1">
                              <AppButton
                                variant="outline-secondary"
                                size="sm"
                                icon="mdi:card-account-details-outline"
                                title="Fiche identifiants"
                                aria-label="Fiche identifiants"
                                onClick={() => setFicheRow(row)}
                              />
                              {row.has_override ? (
                                <AppButton
                                  variant="outline-secondary"
                                  size="sm"
                                  icon="mdi:backup-restore"
                                  iconWidth={16}
                                  title="Revenir au calcul automatique"
                                  aria-label="Revenir au calcul automatique"
                                  disabled={savingId === row.id}
                                  onClick={async () => {
                                    setSavingId(row.id);
                                    try {
                                      const updated = await clientAnnuaireService.reset(row.id);
                                      setClients((prev) => prev.map((item) => (item.id === row.id ? updated : item)));
                                    } catch (err) {
                                      notify.error(err.message || 'Réinitialisation impossible');
                                    } finally {
                                      setSavingId(null);
                                    }
                                  }}
                                />
                              ) : null}
                              {dolibarrCardUrl(row.dolibarr_thirdparty_id) ? (
                                <AppButton
                                  as="a"
                                  variant="outline-secondary"
                                  size="sm"
                                  icon="mdi:open-in-new"
                                  iconWidth={16}
                                  href={dolibarrCardUrl(row.dolibarr_thirdparty_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Ouvrir la fiche Dolibarr"
                                  aria-label="Ouvrir la fiche Dolibarr"
                                />
                              ) : null}
                              {row.is_excluded ? (
                                <AppButton
                                  variant="outline-primary"
                                  size="sm"
                                  title="Réafficher dans l’annuaire. La synchro reprendra cette ligne."
                                  disabled={savingId === row.id}
                                  onClick={async () => {
                                    setSavingId(row.id);
                                    try {
                                      await clientAnnuaireService.restore(row.id);
                                      notify.success('Client restauré');
                                      await loadClients();
                                    } catch (err) {
                                      notify.error(err.message || 'Restauration impossible');
                                    } finally {
                                      setSavingId(null);
                                    }
                                  }}
                                >
                                  Restaurer
                                </AppButton>
                              ) : (
                                <AppButton
                                  variant="outline-danger"
                                  size="sm"
                                  icon="mdi:delete-outline"
                                  iconWidth={16}
                                  title="Masquer. La prochaine synchro ne recréera pas cette ligne."
                                  aria-label="Supprimer"
                                  disabled={savingId === row.id}
                                  onClick={async () => {
                                    const ok = await ask({
                                      title: 'Masquer ce client ?',
                                      message: 'Il disparaîtra de la liste et la prochaine synchro Dolibarr ne le recréera pas.',
                                      confirmLabel: 'Masquer',
                                      confirmVariant: 'danger',
                                    });
                                    if (!ok) {
                                      return;
                                    }
                                    setSavingId(row.id);
                                    try {
                                      await clientAnnuaireService.delete(row.id);
                                      await loadClients();
                                    } catch (err) {
                                      notify.error(err.message || 'Suppression impossible');
                                    } finally {
                                      setSavingId(null);
                                    }
                                  }}
                                />
                              )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </StickyTableResponsive>
                )}
              </div>
              {pagination.total > 0 ? (
                <div className="card-footer">
                  <AppPagination
                    variant="simple"
                    pagination={pagination}
                    totalLabel={`${pagination.total} client${pagination.total > 1 ? 's' : ''}`}
                    onPageChange={setPage}
                  />
                </div>
              ) : null}
                </>
              ) : (
          <ProduitsTab
            produits={produits}
            syncing={syncingProduits}
            onReload={loadProduits}
            onSync={handleSyncProduits}
            onUpdated={(updated) => {
              setProduits((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
            }}
          />
              )}
            </div>
      </div>
    </MasterLayout>
      <ClientAnnuaireExportModal
        show={exportOpen}
        exporting={exporting}
        onClose={() => {
          if (!exporting) setExportOpen(false);
        }}
        onConfirm={handleExport}
      />
      <ClientIdentifiantsDrawer
        show={Boolean(ficheRow)}
        row={ficheRow}
        onClose={() => setFicheRow(null)}
        onSaved={(updated) => {
          if (!updated) return;
          setClients((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
          setFicheRow((current) => (current && current.id === updated.id ? { ...current, ...updated } : current));
        }}
      />
      {modal}
    </>
  );
};

const LEGAL_SOURCE = {
  dolibarr: { label: 'Dolibarr', variant: 'success', title: 'Déjà dans Dolibarr' },
  siret: { label: 'SIRET', variant: 'success', title: 'Déduit du SIRET Dolibarr — cliquer pour saisir' },
  hubspot: { label: 'HubSpot', variant: 'info', title: 'Présent dans HubSpot — cliquer pour saisir' },
};

const pappersSearchUrl = (nom) => {
  const query = encodeURIComponent(String(nom || '').trim());
  return `https://www.pappers.fr/recherche?q=${query}`;
};

const LegalIds = ({ row, onUpdated }) => {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const ignoreBlurRef = useRef(false);

  const fieldValue = (source, field) => (field === 'siren' ? (source?.siren || '') : (source?.tva_intra || ''));
  const fieldLocked = (source, field) => (field === 'siren' ? Boolean(source?.siren_dolibarr) : Boolean(source?.tva_dolibarr));

  const openEditor = async (field) => {
    if (fieldLocked(row, field) || savingRef.current) {
      return;
    }
    const initial = fieldValue(row, field);
    setEditing(field);
    setDraft(initial);
    try {
      const updated = await clientAnnuaireService.refreshLegal(row.id);
      if (!updated) {
        return;
      }
      onUpdated?.(updated);
      if (fieldLocked(updated, field)) {
        setEditing(null);
        return;
      }
      setDraft((current) => (current === initial ? fieldValue(updated, field) : current));
    } catch {
      // La saisie reste ouverte avec la valeur déjà affichée.
    }
  };

  const closeEditor = () => {
    ignoreBlurRef.current = true;
    setEditing(null);
  };

  const saveEditor = async () => {
    if (ignoreBlurRef.current) {
      ignoreBlurRef.current = false;
      return;
    }
    if (!editing || savingRef.current) {
      return;
    }
    const value = editing === 'tva' ? draft.replace(/\s+/g, '').toUpperCase() : draft.replace(/\D+/g, '');
    const current = fieldValue(row, editing);
    if (value === current || value === '') {
      setEditing(null);
      return;
    }
    if (editing === 'siren' && value.length !== 9) {
      notify.error('Le SIREN doit contenir 9 chiffres.');
      return;
    }
    if (editing === 'tva' && !/^[A-Z]{2}[A-Z0-9]{2,13}$/.test(value)) {
      notify.error('La TVA intracom est invalide.');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await clientAnnuaireService.applyPappers(row.id, {
        siren: editing === 'siren' ? value : '',
        tva_intra: editing === 'tva' ? value : '',
      });
      const viaHubspot = result.legal_destination === 'hubspot';
      notify.success(editing === 'siren'
        ? 'SIREN enregistré dans Dolibarr'
        : (viaHubspot
          ? 'TVA enregistrée dans HubSpot. Dolibarr sera mis à jour à la prochaine synchronisation.'
          : 'TVA enregistrée dans Dolibarr'));
      onUpdated?.(result.client);
      setEditing(null);
    } catch (err) {
      notify.error(err.message || 'Enregistrement impossible');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="client-annuaire-legal small mt-1">
      <div className="d-flex flex-column gap-1 flex-grow-1">
        <LegalLine
          field="siren"
          label="SIREN"
          value={row?.siren}
          origine={row?.siren_origine}
          locked={fieldLocked(row, 'siren')}
          editing={editing === 'siren'}
          draft={draft}
          saving={saving}
          onDraft={setDraft}
          onOpen={() => openEditor('siren')}
          onSave={saveEditor}
          onCancel={closeEditor}
        />
        <LegalLine
          field="tva"
          label="TVA"
          value={row?.tva_intra}
          origine={row?.tva_origine}
          locked={fieldLocked(row, 'tva')}
          editing={editing === 'tva'}
          draft={draft}
          saving={saving}
          onDraft={setDraft}
          onOpen={() => openEditor('tva')}
          onSave={saveEditor}
          onCancel={closeEditor}
        />
      </div>
      <AppButton
        as="a"
        variant="outline-secondary"
        size="sm"
        icon="mdi:open-in-new"
        href={pappersSearchUrl(row?.nom)}
        target="_blank"
        rel="noopener noreferrer"
        title="Ouvrir sur Pappers"
        aria-label="Ouvrir sur Pappers"
      />
    </div>
  );
};

const LegalLine = ({
  field,
  label,
  value,
  origine,
  locked,
  editing,
  draft,
  saving,
  onDraft,
  onOpen,
  onSave,
  onCancel,
}) => {
  const source = LEGAL_SOURCE[origine] || null;
  if (editing) {
    return (
      <div className="d-flex flex-wrap align-items-center gap-1">
        <span className="text-secondary-light">{label}</span>
        <AppInput
          size="sm"
          className="client-annuaire-legal-input"
          value={draft}
          autoFocus
          disabled={saving}
          inputMode={field === 'siren' ? 'numeric' : 'text'}
          maxLength={field === 'siren' ? 9 : 32}
          aria-label={label}
          onChange={(event) => onDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              onCancel();
            }
          }}
          onBlur={onSave}
        />
      </div>
    );
  }

  return (
    <div className="d-flex flex-wrap align-items-center gap-1">
      <span className="text-secondary-light">{label}</span>
      {value ? <span>{value}</span> : null}
      {value && source ? (
        <AppBadge
          as={locked ? 'span' : 'button'}
          type={locked ? undefined : 'button'}
          variant={source.variant}
          className={locked ? undefined : 'client-annuaire-legal-edit'}
          title={source.title}
          onClick={locked ? undefined : onOpen}
        >
          {source.label}
        </AppBadge>
      ) : null}
      {!value ? (
        <AppBadge
          as="button"
          type="button"
          variant="warning"
          className="client-annuaire-legal-edit"
          title="Cliquer pour saisir"
          onClick={onOpen}
        >
          manquant
        </AppBadge>
      ) : null}
    </div>
  );
};

const BrandChip = ({ brand, on, overridden, disabled, onToggle }) => {
  const meta = BRANDS[brand];
  const className = [
    'client-annuaire-brand',
    on ? 'client-annuaire-brand--on' : 'client-annuaire-brand--off',
    overridden ? 'client-annuaire-brand--overridden' : '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      title={`${meta.label} : ${on ? 'oui' : 'non'}${overridden ? ' (modifié à la main)' : ''} — cliquer pour inverser`}
      onClick={onToggle}
    >
      {on ? (
        <KkpColorBadge color={meta.color} vivid>
          {meta.short}
        </KkpColorBadge>
      ) : (
        <span className="badge client-annuaire-brand-ghost" style={{ '--brand-color': meta.color }}>
          {meta.short}
        </span>
      )}
    </button>
  );
};

const ContractBadges = ({ contracts }) => {
  if (!contracts.length) {
    return <span className="text-secondary-light">—</span>;
  }

  return (
    <div className="client-annuaire-contracts">
      {contracts.map((contract) => {
        const brand = BRANDS[contract.marque] || BRANDS.kkp;
        const until = contract.until ? formatDate(contract.until) : '';
        const raw = String(contract.label || contract.ref || '').trim();
        const untilSuffix = raw.match(/\s+jusqu.au\s+\d{1,2}\/\d{1,2}\/\d{2,4}\s*$/i);
        const name = (untilSuffix ? raw.slice(0, untilSuffix.index) : raw).trim() || raw;
        return (
          <KkpColorBadge
            key={`${contract.ref}-${contract.until || ''}`}
            color={brand.color}
            vivid
            className="badge client-annuaire-contract-badge"
          >
            <span className="client-annuaire-contract-text" title={[name, until].filter(Boolean).join(' — ')}>
              {name}
              {until ? <span className="client-annuaire-contract-until">{until}</span> : null}
            </span>
          </KkpColorBadge>
        );
      })}
    </div>
  );
};

const OrderBadges = ({ orders }) => {
  if (!orders.length) {
    return <span className="text-secondary-light">—</span>;
  }

  return (
    <div className="client-annuaire-contracts client-annuaire-orders">
      {orders.map((order, index) => {
        const brand = BRANDS[order.marque] || BRANDS.kkp;
        const date = order.date ? formatDate(order.date) : '';
        const name = String(order.label || order.ref || 'Commande').trim();
        return (
          <KkpColorBadge
            key={`${order.ref}-${order.date || ''}-${index}`}
            color={brand.color}
            vivid
            className="badge client-annuaire-contract-badge"
          >
            <span className="client-annuaire-contract-text" title={[name, date].filter(Boolean).join(' — ')}>
              {name}
              {date ? <span className="client-annuaire-contract-until">{date}</span> : null}
            </span>
          </KkpColorBadge>
        );
      })}
    </div>
  );
};

const siteWebHref = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const ContactCell = ({ row, disabled, onSaveEmail, onSavePhone }) => {
  const siteHref = siteWebHref(row.site_web);

  return (
    <div className="client-annuaire-contact">
      <InlineText
        value={row.contact_email}
        href={row.contact_email ? `mailto:${row.contact_email}` : null}
        placeholder="—"
        disabled={disabled}
        onSave={onSaveEmail}
      />
      <InlineText
        value={row.contact_phone}
        href={row.contact_phone ? `tel:${String(row.contact_phone).replace(/\s+/g, '')}` : null}
        placeholder="—"
        disabled={disabled}
        onSave={onSavePhone}
      />
      {siteHref ? (
        <a
          href={siteHref}
          className="client-annuaire-inline-link"
          target="_blank"
          rel="noopener noreferrer"
          title={row.site_web}
          onClick={(event) => event.stopPropagation()}
        >
          {row.site_web}
        </a>
      ) : (
        <span className="text-secondary-light">—</span>
      )}
    </div>
  );
};

const ClientStatutBadge = ({ value, autoValue, overridden, disabled, title, onCycle }) => {
  const meta = CLIENT_STATUT_META[value] || CLIENT_STATUT_META.prospect;
  const next = nextClientStatut(value);
  const nextLabel = CLIENT_STATUT_META[next]?.label || next;

  return (
    <AppBadge
      as="button"
      type="button"
      variant={meta.variant || 'secondary'}
      className={`client-annuaire-yn ${overridden ? 'client-annuaire-yn--overridden' : ''}`}
      title={`${title}${overridden ? ` (forcé, auto : ${CLIENT_STATUT_META[autoValue]?.label || autoValue})` : ''} — cliquer pour passer à « ${nextLabel} »`}
      disabled={disabled}
      onClick={onCycle}
    >
      {meta.label}
    </AppBadge>
  );
};

const CommercialCell = ({ row, commerciaux, disabled, onSave }) => {
  const [editing, setEditing] = useState(false);
  const rootRef = useRef(null);
  const badges = Array.isArray(row.commerciaux) && row.commerciaux.length > 0
    ? row.commerciaux
    : (row.commercial_nom ? [{ nom: row.commercial_nom, couleur: null }] : []);
  const currentId = badges[0]?.id != null ? String(badges[0].id) : '';

  useEffect(() => {
    if (!editing) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      // Portail SearchableSelect (menu hors cellule)
      if (event.target?.closest?.('.searchable-select__menu')) return;
      setEditing(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setEditing(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editing]);

  if (editing) {
    return (
      <div ref={rootRef} className="client-annuaire-commercial-edit">
        <CommercialSelect
          commerciaux={commerciaux}
          value={currentId}
          disabled={disabled}
          buttonClassName="form-select form-select-sm text-start"
          onChange={(next) => {
            const selected = commerciaux.find((item) => String(item.id) === String(next));
            setEditing(false);
            onSave(selected?.nom || '');
          }}
        />
      </div>
    );
  }

  if (badges.length === 0) {
    return (
      <AppButton
        variant="link"
        size="sm"
        className="text-decoration-none p-0"
        disabled={disabled}
        onClick={() => setEditing(true)}
      >
        <span className="text-secondary-light">—</span>
      </AppButton>
    );
  }

  return (
    <AppButton
      variant="link"
      size="sm"
      className="text-decoration-none p-0 client-annuaire-commercial"
      disabled={disabled}
      onClick={() => setEditing(true)}
      title={row.commercial_nom_auto && row.commercial_nom !== row.commercial_nom_auto
        ? `Auto Dolibarr : ${row.commercial_nom_auto}`
        : undefined}
    >
      {badges.map((item) => (
        <KkpColorBadge key={`${item.id || item.nom}`} color={item.couleur} vivid>
          {item.nom}
        </KkpColorBadge>
      ))}
    </AppButton>
  );
};

const InlineText = ({ value, disabled, onSave, href = null, placeholder = '—' }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  if (!editing) {
    const empty = !value;
    return (
      <span className="client-annuaire-inline">
        {href && !empty ? (
          <a href={href} className="client-annuaire-inline-link" onClick={(event) => event.stopPropagation()}>
            {value}
          </a>
        ) : (
          <AppButton
            variant="link"
            size="sm"
            className="text-start text-decoration-none p-0"
            disabled={disabled}
            onClick={() => setEditing(true)}
          >
            {value || <span className="text-secondary-light">{placeholder}</span>}
          </AppButton>
        )}
        {href && !empty ? (
          <AppButton
            variant="link"
            size="sm"
            className="p-0 client-annuaire-inline-edit"
            disabled={disabled}
            title="Modifier"
            icon="mdi:pencil-outline"
            iconWidth={14}
            onClick={() => setEditing(true)}
          />
        ) : null}
      </span>
    );
  }

  return (
    <AppInput
      size="sm"
      className="client-annuaire-inline-input"
      value={draft}
      autoFocus
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        if ((draft || '') !== (value || '')) {
          onSave(draft.trim());
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          setDraft(value || '');
          setEditing(false);
        }
      }}
    />
  );
};

const ProduitsTab = ({ produits, syncing, onReload, onSync, onUpdated }) => {
  const { ask, modal } = useAppConfirm();
  const [search, setSearch] = useState('');
  const [etat, setEtat] = useState([]);
  const [marque, setMarque] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const selectedEtat = firstOrEmpty(etat);
    const selectedMarques = new Set(marque.map((m) => String(m).trim()).filter(Boolean));
    return produits.filter((produit) => {
      if (produit.is_excluded) return false;
      if (selectedEtat === '1' && !produit.is_tosell) return false;
      if (selectedEtat === '0' && produit.is_tosell) return false;
      if (selectedMarques.size) {
        const match =
          (selectedMarques.has('iti') && produit.is_iti)
          || (selectedMarques.has('kkp') && produit.is_kkp)
          || (selectedMarques.has('klub') && produit.is_klub);
        if (!match) return false;
      }
      if (!q) return true;
      const haystack = `${produit.product_ref || ''} ${produit.product_label || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [produits, search, etat, marque]);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(filtered, PRODUIT_SORT_COLUMNS);
  const hasActiveFilters = Boolean(
    search
    || marque.length
    || firstOrEmpty(etat) !== '',
  );

  const patch = async (id, body) => {
    setSavingId(id);
    try {
      const updated = await clientAnnuaireService.updateProduit(id, body);
      if (updated) {
        onUpdated?.(updated);
      } else {
        await onReload();
      }
    } catch (err) {
      notify.error(err.message || 'Mise à jour impossible');
    } finally {
      setSavingId(null);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setEtat([]);
    setMarque([]);
  };

  return (
    <>
      <AppFilterBar
        className="mb-0 client-annuaire-product-filters"
        onReset={resetFilters}
        resetDisabled={!hasActiveFilters}
      >
        <AppFilterInput
          id="produits-search"
          label="Recherche"
          placeholder="Référence ou libellé"
          value={search}
          onChange={setSearch}
        />
        <AppFilterSelect
          label="État (vente)"
          value={etat}
          onChange={setEtat}
          options={ETAT_VENTE}
          multiple={false}
          placeholder="Tous"
        />
        <AppFilterSelect
          label="Marque"
          value={marque}
          onChange={setMarque}
          options={MARQUES}
          multiple
          placeholder="Toutes"
        />
      </AppFilterBar>
      <div className="card-body p-0">
        {sortedRows.length === 0 ? (
          <KkpPlanningEmptyState
            message={produits.length === 0
              ? 'Aucun produit. Synchronisez le catalogue Dolibarr.'
              : 'Aucun produit ne correspond à ces filtres.'}
            actions={produits.length === 0 ? [{
              key: 'sync',
              label: syncing ? 'Synchronisation…' : 'Synchroniser le catalogue',
              icon: 'mdi:sync',
              variant: 'primary',
              onClick: onSync,
              disabled: syncing,
            }] : [{
              key: 'reset',
              label: 'Réinitialiser les filtres',
              icon: 'mdi:filter-off-outline',
              onClick: resetFilters,
            }]}
          />
        ) : (
          <StickyTableResponsive>
            <table className="table table-hover mb-0 kkp-iti-list-table">
              <thead>
                <tr>
                  <SortableTh sortKey="ref" label="Réf." sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="label" label="Libellé" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="price" label="Prix HT" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="tva" label="TVA %" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="etat" label="État (vente)" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <th>Marques</th>
                  <SortableTh sortKey="duree" label="Périodicité" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="synced" label="Sync" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((produit) => (
                  <tr key={produit.id}>
                    <td><code>{produit.product_ref}</code></td>
                    <td>{produit.product_label || '—'}</td>
                    <td className="text-nowrap">{formatPriceHt(produit.price_ht)}</td>
                    <td className="text-nowrap">
                      {produit.tva_tx === null || produit.tva_tx === undefined || produit.tva_tx === ''
                        ? '—'
                        : `${Number(produit.tva_tx).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`}
                    </td>
                    <td>
                      <AppStatusBadge
                        status={produit.is_tosell ? 'active' : 'invalid'}
                        label={produit.is_tosell ? 'En vente' : 'Hors vente'}
                      />
                    </td>
                    <td>
                      <div className="client-annuaire-brands">
                        {['iti', 'kkp', 'klub'].map((brand) => (
                          <BrandChip
                            key={brand}
                            brand={brand}
                            on={Boolean(produit[`is_${brand}`])}
                            overridden={Boolean(produit.manual)}
                            disabled={savingId === produit.id}
                            onToggle={() => patch(produit.id, { [`is_${brand}`]: !produit[`is_${brand}`] })}
                          />
                        ))}
                      </div>
                    </td>
                    <td>
                      {produit.duration_value
                        ? `${produit.duration_value} ${DURATION_UNITS.find((unit) => unit.value === produit.duration_unit)?.label ?? produit.duration_unit}`
                        : 'Ponctuel'}
                    </td>
                    <td className="text-nowrap small text-secondary-light" title={produit.synced_at || ''}>
                      {formatDateTime(produit.synced_at)}
                    </td>
                    <td className="text-end">
                      {produit.is_excluded ? (
                        <AppButton
                          variant="outline-primary"
                          size="sm"
                          disabled={savingId === produit.id}
                          title="Réafficher dans le catalogue. La synchro reprendra cette ligne."
                          onClick={async () => {
                            setSavingId(produit.id);
                            try {
                              const updated = await clientAnnuaireService.restoreProduit(produit.id);
                              onUpdated?.(updated);
                              notify.success('Produit restauré');
                            } catch (err) {
                              notify.error(err.message || 'Restauration impossible');
                            } finally {
                              setSavingId(null);
                            }
                          }}
                        >
                          Restaurer
                        </AppButton>
                      ) : (
                        <AppButton
                          variant="outline-danger"
                          size="sm"
                          icon="mdi:delete-outline"
                          iconWidth={16}
                          disabled={savingId === produit.id}
                          title="Masquer. La prochaine synchro ne recréera pas cette ligne."
                          aria-label="Supprimer"
                          onClick={async () => {
                            const ok = await ask({
                              title: 'Masquer ce produit ?',
                              message: 'Il disparaîtra de la liste et la prochaine synchro Dolibarr ne le recréera pas.',
                              confirmLabel: 'Masquer',
                              confirmVariant: 'danger',
                            });
                            if (!ok) {
                              return;
                            }
                            setSavingId(produit.id);
                            try {
                              const updated = await clientAnnuaireService.deleteProduit(produit.id);
                              onUpdated?.(updated);
                            } catch (err) {
                              notify.error(err.message || 'Suppression impossible');
                            } finally {
                              setSavingId(null);
                            }
                          }}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StickyTableResponsive>
        )}
      </div>
      <div className="card-footer text-secondary-light small">
        {sortedRows.length} / {produits.length} produit{produits.length > 1 ? 's' : ''}
        {firstOrEmpty(etat) === '1' ? ' — filtre En vente' : null}
      </div>
      {modal}
    </>
  );
};

export default ClientAnnuairePage;
